import React, { useState } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Card, 
  CardContent, 
  Grid, 
  Chip, 
  Button,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Divider
} from '@mui/material';
import { 
  TrendingUp, 
  AccountBalance,
  Security,
  Timeline,
  People,
  AttachMoney,
  CheckCircle,
  Info
} from '@mui/icons-material';

const InvestmentFunds = () => {
  const [selectedFund, setSelectedFund] = useState(null);

  const investmentFunds = [
    {
      id: 1,
      name: "DS360 Foreclosure Recovery Fund I",
      type: "Diversified Real Estate",
      targetReturn: "15-18%",
      minimumInvestment: "$25,000",
      currentSize: "$12.5M",
      targetSize: "$25M",
      progress: 50,
      status: "Open",
      strategy: "Multi-property acquisition and rehabilitation in prime NJ markets",
      riskLevel: "Medium",
      term: "3-5 years",
      distributions: "Quarterly",
      highlights: [
        "Professional property management",
        "Diversified across 50+ properties",
        "Experienced acquisition team",
        "Proven track record"
      ],
      returns: {
        year1: "12%",
        year2: "16%",
        year3: "18%",
        cumulative: "52%"
      }
    },
    {
      id: 2,
      name: "Premium Value-Add Fund",
      type: "Value-Add Commercial",
      targetReturn: "20-25%",
      minimumInvestment: "$50,000",
      currentSize: "$8.2M",
      targetSize: "$15M",
      progress: 55,
      status: "Open",
      strategy: "Acquire distressed commercial properties for renovation and repositioning",
      riskLevel: "Medium-High",
      term: "2-4 years",
      distributions: "Semi-Annual",
      highlights: [
        "Focus on high-growth submarkets",
        "Institutional-grade due diligence",
        "Value-creation through improvements",
        "Strong exit opportunities"
      ],
      returns: {
        year1: "8%",
        year2: "22%",
        year3: "28%",
        cumulative: "68%"
      }
    },
    {
      id: 3,
      name: "Income Plus Residential Fund",
      type: "Cash Flow Focused",
      targetReturn: "12-15%",
      minimumInvestment: "$10,000",
      currentSize: "$5.8M",
      targetSize: "$10M",
      progress: 58,
      status: "Open",
      strategy: "Acquire stabilized rental properties for consistent cash flow",
      riskLevel: "Low-Medium",
      term: "5-7 years",
      distributions: "Monthly",
      highlights: [
        "Steady monthly income",
        "Conservative underwriting",
        "Class B neighborhoods",
        "Long-term appreciation"
      ],
      returns: {
        year1: "11%",
        year2: "13%",
        year3: "14%",
        cumulative: "42%"
      }
    }
  ];

  const investmentProcess = [
    {
      step: 1,
      title: "Review Fund Details",
      description: "Examine fund strategy, returns, risk profile, and terms"
    },
    {
      step: 2,
      title: "Qualify as Investor",
      description: "Complete accredited investor verification process"
    },
    {
      step: 3,
      title: "Submit Investment",
      description: "Submit subscription documents and transfer funds"
    },
    {
      step: 4,
      title: "Receive Updates",
      description: "Get quarterly reports and distribution payments"
    }
  ];

  const performanceMetrics = [
    { metric: "Average Annual Return", value: "16.2%", period: "3-Year Track Record" },
    { metric: "Total Assets Under Management", value: "$45.8M", period: "Current Portfolio" },
    { metric: "Properties Acquired", value: "127", period: "Since 2020" },
    { metric: "Investor Satisfaction", value: "94%", period: "Latest Survey" }
  ];

  const getRiskColor = (risk) => {
    switch (risk) {
      case 'Low': return 'success';
      case 'Low-Medium': return 'info';
      case 'Medium': return 'warning';
      case 'Medium-High': return 'error';
      case 'High': return 'error';
      default: return 'default';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Open': return 'success';
      case 'Closing Soon': return 'warning';
      case 'Closed': return 'default';
      default: return 'default';
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa', py: 4 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h2" component="h1" sx={{ 
            fontWeight: 700, 
            color: '#1B5E20',
            mb: 2 
          }}>
            Investment Funds
          </Typography>
          <Typography variant="h6" sx={{ 
            color: '#666',
            maxWidth: '800px',
            mx: 'auto',
            lineHeight: 1.6
          }}>
            Invest in professionally managed real estate funds for diversified exposure 
            to foreclosure and distressed property opportunities.
          </Typography>
        </Box>

        {/* Important Notice */}
        <Alert severity="warning" sx={{ mb: 4 }}>
          <Typography variant="body1">
            <strong>Accredited Investors Only:</strong> These investment opportunities are available 
            only to accredited investors as defined by SEC regulations. All investments carry risk 
            of loss and past performance does not guarantee future results.
          </Typography>
        </Alert>

        {/* Performance Overview */}
        <Paper sx={{ p: 4, mb: 6, bgcolor: '#e8f5e8' }}>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 4, color: '#1B5E20', textAlign: 'center' }}>
            Fund Performance Overview
          </Typography>
          <Grid container spacing={3}>
            {performanceMetrics.map((metric, index) => (
              <Grid item xs={12} md={3} key={index}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#1B5E20', mb: 1 }}>
                    {metric.value}
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    {metric.metric}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666' }}>
                    {metric.period}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Paper>

        {/* Available Funds */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 4, color: '#1B5E20' }}>
            Available Investment Funds
          </Typography>
          <Grid container spacing={4}>
            {investmentFunds.map((fund) => (
              <Grid item xs={12} lg={4} key={fund.id}>
                <Card sx={{ 
                  height: '100%',
                  transition: 'all 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 25px rgba(0,0,0,0.12)'
                  }
                }}>
                  <CardContent sx={{ p: 3 }}>
                    {/* Fund Header */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Chip 
                        label={fund.status}
                        color={getStatusColor(fund.status)}
                        size="small"
                      />
                      <Chip 
                        label={fund.type}
                        variant="outlined"
                        size="small"
                      />
                    </Box>

                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1B5E20' }}>
                      {fund.name}
                    </Typography>

                    <Typography variant="body2" sx={{ color: '#666', mb: 3, lineHeight: 1.6 }}>
                      {fund.strategy}
                    </Typography>

                    {/* Key Metrics */}
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                      <Grid item xs={6}>
                        <Typography variant="caption" sx={{ color: '#666' }}>Target Return</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                          {fund.targetReturn}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" sx={{ color: '#666' }}>Min Investment</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {fund.minimumInvestment}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" sx={{ color: '#666' }}>Term</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {fund.term}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" sx={{ color: '#666' }}>Risk Level</Typography>
                        <Chip 
                          label={fund.riskLevel} 
                          size="small" 
                          color={getRiskColor(fund.riskLevel)}
                        />
                      </Grid>
                    </Grid>

                    {/* Fund Progress */}
                    <Box sx={{ mb: 3 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="caption" sx={{ color: '#666' }}>Fund Progress</Typography>
                        <Typography variant="caption" sx={{ color: '#666' }}>
                          {fund.currentSize} / {fund.targetSize}
                        </Typography>
                      </Box>
                      <LinearProgress 
                        variant="determinate" 
                        value={fund.progress}
                        sx={{ 
                          height: 8, 
                          borderRadius: 4,
                          bgcolor: '#e0e0e0',
                          '& .MuiLinearProgress-bar': {
                            bgcolor: '#1B5E20'
                          }
                        }}
                      />
                    </Box>

                    {/* Highlights */}
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="caption" sx={{ color: '#666', mb: 1, display: 'block' }}>
                        Key Highlights
                      </Typography>
                      {fund.highlights.slice(0, 2).map((highlight, index) => (
                        <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                          <CheckCircle sx={{ fontSize: 14, color: '#2e7d32', mr: 1 }} />
                          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                            {highlight}
                          </Typography>
                        </Box>
                      ))}
                    </Box>

                    {/* Action Buttons */}
                    <Button 
                      variant="contained" 
                      fullWidth
                      sx={{ 
                        bgcolor: '#1B5E20',
                        mb: 1,
                        '&:hover': { bgcolor: '#0d3f16' }
                      }}
                      onClick={() => setSelectedFund(fund)}
                    >
                      View Details
                    </Button>
                    <Button 
                      variant="outlined" 
                      fullWidth
                      size="small"
                      sx={{ color: '#1B5E20', borderColor: '#1B5E20' }}
                    >
                      Download Prospectus
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Investment Process */}
        <Paper sx={{ p: 4, mb: 6 }}>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 4, color: '#1B5E20', textAlign: 'center' }}>
            Investment Process
          </Typography>
          <Grid container spacing={4}>
            {investmentProcess.map((step, index) => (
              <Grid item xs={12} md={3} key={index}>
                <Box sx={{ textAlign: 'center' }}>
                  <Box sx={{ 
                    width: 60, 
                    height: 60, 
                    borderRadius: '50%', 
                    bgcolor: '#1B5E20', 
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.5rem',
                    fontWeight: 'bold',
                    mx: 'auto',
                    mb: 2
                  }}>
                    {step.step}
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    {step.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666' }}>
                    {step.description}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Paper>

        {/* Benefits of Fund Investment */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 4, color: '#1B5E20' }}>
            Benefits of Fund Investment
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 3 }}>
                <People sx={{ fontSize: 48, color: '#1B5E20', mb: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  Professional Management
                </Typography>
                <Typography variant="body2" sx={{ color: '#666' }}>
                  Experienced team handles acquisition, management, and disposition 
                  of properties on your behalf.
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 3 }}>
                <Security sx={{ fontSize: 48, color: '#1B5E20', mb: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  Diversification
                </Typography>
                <Typography variant="body2" sx={{ color: '#666' }}>
                  Spread risk across multiple properties and markets rather than 
                  relying on a single investment.
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 3 }}>
                <Timeline sx={{ fontSize: 48, color: '#1B5E20', mb: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  Passive Income
                </Typography>
                <Typography variant="body2" sx={{ color: '#666' }}>
                  Receive regular distributions without the day-to-day management 
                  responsibilities of property ownership.
                </Typography>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* Call to Action */}
        <Box sx={{ 
          textAlign: 'center', 
          bgcolor: '#1B5E20', 
          color: 'white',
          borderRadius: 3,
          p: 4
        }}>
          <AccountBalance sx={{ fontSize: 60, mb: 2, opacity: 0.9 }} />
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
            Ready to Diversify Your Portfolio?
          </Typography>
          <Typography variant="h6" sx={{ mb: 3, opacity: 0.9 }}>
            Join our network of sophisticated investors accessing institutional-quality real estate opportunities
          </Typography>
          <Button 
            variant="contained" 
            size="large"
            sx={{ 
              bgcolor: 'white',
              color: '#1B5E20',
              fontWeight: 600,
              px: 4,
              mr: 2
            }}
          >
            Schedule Consultation
          </Button>
          <Button 
            variant="outlined" 
            size="large"
            sx={{ 
              color: 'white',
              borderColor: 'white',
              fontWeight: 600,
              px: 4
            }}
          >
            Download Information Package
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default InvestmentFunds;














